import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Copy, Check, ArrowLeft, Sparkles } from "lucide-react";
import { aiAPI } from "../../api";
import Chip from "../../components/atoms/Chip";
import Button from "../../components/atoms/Button";

export default function EmailAssistant() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [context, setContext] = useState("");
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    aiAPI.emailTemplates().then((d) => setTemplates(d.templates)).catch((e) => setError(e.message));
  }, []);

  async function generate() {
    if (!selected) return;
    setError(null);
    setLoading(true);
    setEmail(null);
    try {
      const data = await aiAPI.emailDraft(selected, context);
      setEmail(data.email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function copy() {
    if (!email) return;
    navigator.clipboard?.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/write" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Write
        </Link>
        <Chip tone="rust">Email Assistant</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          Send the email that <em style={{ color: "var(--gold-500)" }}>actually lands.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "62ch" }}>
          Pick a situation, add a sentence or two of context. Get a draft you can send.
        </p>
      </div>

      {/* Template grid */}
      <div>
        <p className="eyebrow" style={{ marginBottom: 10 }}>Pick a situation</p>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelected(t.id)}
              style={{
                textAlign: "left",
                padding: "12px 14px",
                background: selected === t.id ? "var(--ink-900)" : "#fff",
                color: selected === t.id ? "var(--paper)" : "var(--fg-1)",
                border: `1px solid ${selected === t.id ? "var(--ink-900)" : "var(--border-2)"}`,
                borderRadius: "var(--radius-md)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Context input */}
      {selected && (
        <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-2)" }}>
            Optional context (helps the draft fit your situation)
          </label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. Need 3 days off for my sister's wedding, Aug 12–14. I've finished my current sprint."
            rows={3}
            style={{
              width: "100%", fontFamily: "var(--font-body)", fontSize: 14, lineHeight: 1.55,
              border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)",
              padding: "10px 12px", resize: "vertical", background: "#fff", color: "var(--fg-1)", outline: "none",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--fg-3)" }}>{context.length} / 800</span>
            <Button variant="primary" onClick={generate} disabled={loading} iconLeft={<Sparkles size={15} strokeWidth={1.8} />}>
              {loading ? "Drafting…" : "Draft email"}
            </Button>
          </div>
          {error && <p style={{ color: "var(--berry)", fontSize: 13, margin: 0 }}>{error}</p>}
        </div>
      )}

      {/* Result */}
      {email && (
        <article style={{ background: "#fff", border: "1px solid var(--border-1)", borderTop: "3px solid var(--rust)", borderRadius: "var(--radius-lg)", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Mail size={18} strokeWidth={1.6} color="var(--rust)" />
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-2)" }}>Draft</span>
            </div>
            <button type="button" onClick={copy} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", fontSize: 12, fontWeight: 600,
              background: copied ? "var(--sage)" : "#fff",
              color: copied ? "var(--paper)" : "var(--fg-2)",
              border: `1px solid ${copied ? "var(--sage)" : "var(--border-2)"}`,
              borderRadius: 999, cursor: "pointer",
            }}>
              {copied ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.6} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <pre style={{
            fontFamily: "var(--font-body)", fontSize: 15, lineHeight: 1.65,
            color: "var(--fg-1)", margin: 0, whiteSpace: "pre-wrap",
          }}>{email}</pre>
        </article>
      )}
    </div>
  );
}
